/* =====================================================================
 * Entry Point
 * ===================================================================== */

/* Phase 11-4b-tenant: bootstrap the active-tenant registry BEFORE any
 * other import. The imported module's top-level body calls
 * `setActiveTenantManifest()` so shared packages (workspace-utils,
 * i18n, …) can resolve tenant-scoped storage keys at their own
 * module eval.
 *
 * IMPORTANT — must be a function call, not a side-effect or const
 * import:
 *   - A naked `import './tenant/storage'` is a side-effect-only
 *     import that Vite/Rollup are free to hoist into a secondary
 *     chunk under our `manualChunks` configuration.
 *   - A `const __MARKER__ = true` re-export gets inlined by Terser
 *     and dead-code-eliminated, allowing the same hoist.
 *   In both cases `setActiveTenantManifest()` ends up running at
 *   lazy-chunk eval time — too late, since `@auw/i18n`'s module body
 *   already called `getActiveTenantManifest()` and threw "Active
 *   tenant manifest has not been set". (2026-04-29 dev.askyour.ai
 *   blank-screen regression.)
 *
 *   Calling a real function with its return value used downstream
 *   forces the bundler to keep `./tenant/storage` in the entry
 *   chunk's eval graph and pins its eval order to "before every
 *   other import below". */
import { getBootstrappedTenantId } from './tenant/storage';
const __auwBootstrappedTenantId = getBootstrappedTenantId();
if (typeof __auwBootstrappedTenantId !== 'string' || __auwBootstrappedTenantId.length === 0) {
  throw new Error('[bootstrap] tenant registry failed to initialize');
}

import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthProviderRegistry } from '@auw/auth';
import { setTenantSnapshot } from '@auw/onprem';
import { TenantProvider } from '@auw/tenant';
import { FeatureFlagProvider, type FeatureFlagMap } from '@auw/feature-flags';
import App from './App';
import {
  queryClient,
  hydrateFromSessionStorage,
  dehydrateToSessionStorage,
} from '@/query/client';
import { initErrorMonitoring } from '@/monitoring/sentry';
import { initPerfMetricCollector } from '@/monitoring/perfMetricCollector';
import {
  AppErrorBoundary,
  SettingsModal,
  restoreCookieConsentToSink,
  setCookieConsentSink,
  setDocxPreviewRenderer,
  setSettingsModalComponent,
  setSpecializedAgentPanelComponent,
  setSpecializedStorageComponents,
} from '@auw/workspace-ui';
import {
  setDocAgentEditorPageComponent,
  setDocAgentLandingPageComponent,
  setDocAgentModePageComponent,
} from '@auw/workspace-features';
import { updateGA4Consent } from '@/monitoring/ga4';

/* Phase 11-4h: register host-specific mode components into shared DI
 * slots so the workspace router / chat gear button can render them
 * without a direct host-path import.
 *
 *  - SettingsModal: B2C ships the full GDPR cookie + legal-versions
 *    panel; on-prem tenants inject a lighter corporate variant or leave
 *    the slot empty (gear button silently no-ops).
 *  - DocAgent pages: the 410-file docx editor bundle lives in
 *    `features/docAgent/` and is still host-local pending a separate
 *    migration pass.  Route-first layout gets landing/editor slots,
 *    while the legacy mode-page slot remains as a compatibility fallback. */
setSettingsModalComponent(SettingsModal);
const LazyDocAgentModePage = React.lazy(
  () => import('@/features/modes/doc-agent/DocAgentModePage'),
);
const LazyDocAgentLandingPage = React.lazy(
  () => import('@/features/modes/doc-agent/DocAgentModePage')
    .then((m) => ({ default: m.DocAgentLandingRoutePage })),
);
const LazyDocAgentEditorPage = React.lazy(
  () => import('@/features/modes/doc-agent/DocAgentModePage')
    .then((m) => ({ default: m.DocAgentEditorRoutePage })),
);
setDocAgentModePageComponent(LazyDocAgentModePage);
setDocAgentLandingPageComponent(LazyDocAgentLandingPage);
setDocAgentEditorPageComponent(LazyDocAgentEditorPage);

/* Integrated/file-preview DOCX rendering uses the same vendor paged editor as
 * doc-agent, mounted read-only. `workspace-ui` owns FilePreviewPanel, so the
 * host registers this renderer through a slot instead of making the shared
 * package import host-local docAgent code. */
const LazyDocAgentReadOnlyDocxPreview = React.lazy(
  () => import('@/features/docAgent/components/editors/DocAgentReadOnlyDocxPreview')
    .then((m) => ({ default: m.DocAgentReadOnlyDocxPreview })),
);
setDocxPreviewRenderer(LazyDocAgentReadOnlyDocxPreview);

/* StoragePage 에서 "전문 에이전트 > CAD 에이전트" 선택 시 임베드되는
 * 패널 (풀스크린이 아닌 임베드 컨텍스트가 따로 필요할 때 사용 — 현재는
 * storage 의 메인 영역에서 Tree+List 컴포넌트로 분리 렌더한다). */
const LazyCADAgentEmbeddedPanel = React.lazy(
  () => import('@/features/specialized/cad/CADAgentEmbeddedPanel'),
);
setSpecializedAgentPanelComponent('cad', LazyCADAgentEmbeddedPanel);

/* StoragePage 의 사이드 트리 + 메인 리스트 — storage 의 다른 섹션과
 * 동일한 컬럼/스타일을 사용하므로 사용자는 CAD 파일도 일반 워크스페이스
 * 파일과 같은 모양으로 본다. */
const LazyCadStorageTree = React.lazy(
  () => import('@/features/specialized/cad/CadStorageTree')
    .then((m) => ({ default: m.CadStorageTree })),
);
const LazyCadStorageList = React.lazy(
  () => import('@/features/specialized/cad/CadStorageList')
    .then((m) => ({ default: m.CadStorageList })),
);
setSpecializedStorageComponents('cad', {
  Tree: LazyCadStorageTree,
  List: LazyCadStorageList,
});

/* 견적 에이전트 — CAD 와 동일한 임베드 패널 + storage 트리/리스트 DI.
 * CAD 코드에 의존하지 않고 별도 estimate 도메인 컴포넌트를 등록한다. */
const LazyEstimateAgentEmbeddedPanel = React.lazy(
  () => import('@/features/specialized/estimate/EstimateAgentEmbeddedPanel'),
);
setSpecializedAgentPanelComponent('estimate', LazyEstimateAgentEmbeddedPanel);

const LazyEstimateStorageTree = React.lazy(
  () => import('@/features/specialized/estimate/EstimateStorageTree')
    .then((m) => ({ default: m.EstimateStorageTree })),
);
const LazyEstimateStorageList = React.lazy(
  () => import('@/features/specialized/estimate/EstimateStorageList')
    .then((m) => ({ default: m.EstimateStorageList })),
);
setSpecializedStorageComponents('estimate', {
  Tree: LazyEstimateStorageTree,
  List: LazyEstimateStorageList,
});

/* 문서 에이전트 — CAD/견적과 동일하게 파일 저장소 페이지에서 계층(프로젝트/
 * 폴더) 트리/리스트로 보이도록 슬롯에 등록한다. 문서는 specialized 가 아니라
 * 개인 workspace 의 'doc-agent' 모드라, 슬롯 id 도 모드 id('doc-agent') 를
 * 사용한다(StorageSidebar/StoragePage 가 모드별 슬롯도 해석하도록 일반화됨). */
const LazyDocStorageTree = React.lazy(
  () => import('@/features/docAgent/components/landing/DocStorageTree')
    .then((m) => ({ default: m.DocStorageTree })),
);
const LazyDocStorageList = React.lazy(
  () => import('@/features/docAgent/components/landing/DocStorageList')
    .then((m) => ({ default: m.DocStorageList })),
);
setSpecializedStorageComponents('doc-agent', {
  Tree: LazyDocStorageTree,
  List: LazyDocStorageList,
});
import { scheduleRuntimeFontLoading } from '@auw/workspace-utils';
import { resolveTenantManifest } from '@/tenant';
import { buildTenantAuthProviders } from '@/auth/providers';
import { AppPermissionProvider } from '@/permissions/AppPermissionProvider';

/* i18n — 앱 전역 다국어 초기화 (다른 모듈보다 먼저 로드) */
import '@auw/i18n';

/* Temml CSS — 시스템 폰트 사용 (웹폰트 로딩 불필요, 가장 가벼움) */
import 'temml/dist/Temml-Local.css';

/* NOTE: DocAgent 전용 CSS (`@/features/docAgent/vendor/docxEditor/index.css`)
 * 는 본 파일이 아니라 실제 사용 청크에서 import 한다.
 *   - DocAgentModePage: 문서에이전트 편집 페이지
 *   - DocAgentReadOnlyDocxPreview: 통합/공통 파일 미리보기의 DOCX read-only
 *     렌더러
 * 해당 CSS 는 `.ep-root` / `.docx-*` / `.layout-*` / `.ProseMirror-*` 등
 * 에디터 전용 셀렉터로만 구성되어 DOCX 화면을 열 때만 필요하다. */

/* DocAgent dev-only 전역 디버그 훅 (window.__docAgentDebug).
 *
 * devGlobal 모듈은 `docxStack`, `pagedEditor`, `vendor/docxEditor` 등을
 * top-level 로 import 하므로 top-level 정적 import 로 두면 production 번들
 * 에도 해당 모듈 트리가 모두 포함된다. `import.meta.env.DEV` 는 런타임
 * 분기이지 tree-shake 조건이 아니라, 분기 내부의 정적 import 도 같은
 * 문제를 갖는다. 따라서 dev 에서만 동적 import 로 비동기 로드해 초기
 * 앱 번들과 분리하고, production 번들에서는 아예 진입하지 않는다. */
if (import.meta.env.DEV) {
  void import('@/features/docAgent/poc/devGlobal').catch((err) => {
    // dev 디버그 훅 로드 실패는 앱 동작에 영향 없으므로 조용히 로그만.
    // eslint-disable-next-line no-console
    console.warn('[doc-agent] devGlobal 로드 실패', err);
  });
}

/* Phase 12-5b: register the GA4 dispatcher into the shared
 * cookie-consent hook BEFORE we replay any persisted state. Enterprise
 * hosts skip both calls — they ship no analytics integration. */
setCookieConsentSink(updateGA4Consent);

/* Phase 8: publish the resolved tenant to `@auw/onprem`'s module-scope
 * snapshot BEFORE any non-React loader runs (sentry/ga4/paddle/font
 * loader read it back). This is the single source of truth for tenant-
 * aware decisions outside the React tree. */
const activeTenant = resolveTenantManifest();
setTenantSnapshot(activeTenant);

initErrorMonitoring();
initPerfMetricCollector();
restoreCookieConsentToSink();

const scheduleQueryHydration = () => {
  const runHydration = () => hydrateFromSessionStorage();
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(() => {
      runHydration();
    }, { timeout: 900 });
    return;
  }
  window.setTimeout(runHydration, 0);
};

scheduleQueryHydration();
window.addEventListener('beforeunload', dehydrateToSessionStorage);

const tenantFeatureFlags = activeTenant.features as FeatureFlagMap | undefined;
const tenantAuthProviders = buildTenantAuthProviders(activeTenant);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <TenantProvider manifest={activeTenant}>
      <FeatureFlagProvider flags={tenantFeatureFlags}>
        <AuthProviderRegistry providers={tenantAuthProviders}>
          <QueryClientProvider client={queryClient}>
            <AppPermissionProvider>
              <AppErrorBoundary name="root-app">
                <App />
              </AppErrorBoundary>
            </AppPermissionProvider>
          </QueryClientProvider>
        </AuthProviderRegistry>
      </FeatureFlagProvider>
    </TenantProvider>
  </React.StrictMode>,
);

scheduleRuntimeFontLoading();

/* DocAgent 진입 청크 prefetch
 *
 * DocAgentModePage 는 `React.lazy` 로 라우트 청크가 분리되어 있어 실제
 * 탭 진입 시점에 JS + CSS 를 받는다. 번들 크기가 크기 때문에 네트워크
 * 상태에 따라 수백 ms ~ 수 초 대기가 발생한다. 앱이 idle 해진 시점에
 * 미리 한 번 import 해 두면 실제 진입 시 캐시 히트로 즉시 렌더된다.
 * 이미 방문 중이거나 Vite 내부 캐시에 올라온 경우 추가 네트워크 요청은
 * 발생하지 않는다 (동일 Promise 재사용). 실패는 조용히 무시.
 */
const prefetchDocAgentEntryChunk = () => {
  void import('@/features/modes/doc-agent/DocAgentModePage').catch(() => {
    /* prefetch 실패는 진짜 진입 시점의 lazy import 에 맡긴다. */
  });
};
if (typeof window.requestIdleCallback === 'function') {
  window.requestIdleCallback(prefetchDocAgentEntryChunk, { timeout: 10_000 });
} else {
  window.setTimeout(prefetchDocAgentEntryChunk, 4000);
}

